// WikiAgent.jsx — per-department grounded chat agent (docked 380px or full-page).
import React, { useRef, useEffect } from 'react';
import { Maximize2, Minimize2, X, MessageCircleQuestion } from 'lucide-react';
import { DEPT_BY_ID } from '../../lib/wikiConstants';
import { useWikiApi } from '../../hooks/useWiki';
import { WikiAiAvatar } from './WikiCiteChip';
import { Bubble, Composer, useWikiChat } from './WikiChat';

const SUGGESTIONS = {
    marketing: ['What’s our brand voice in one line?', 'Can I use the gradient as a page background?', 'What font sizes should body copy use?'],
    sales: ['What has to be true before I leave a discovery call?', 'How should I approach negotiation?'],
    service: ['What refund can I approve without sign-off?', 'When do I escalate a ticket?'],
    company: ['How much annual leave do I get?', 'What tools do I get access to?'],
};
const GENERIC = ['What are the key policies here?', 'What do I need to read first?', 'Summarize the most important page.'];

export default function WikiAgent({ deptId, docked, fullPage, onToggleFull, onClose, onOpenDoc, userName }) {
    const api = useWikiApi();
    const dept = DEPT_BY_ID[deptId];
    const { msgs, send } = useWikiChat((text) => api.agentChat({ dept: deptId, question: text }), `the ${dept?.name} wiki`);
    const bodyRef = useRef(null);
    useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);
    const suggestions = SUGGESTIONS[deptId] || GENERIC;

    return (
        <div className="h-full flex flex-col bg-stone-50">
            <div className="h-14 px-4 flex items-center gap-2.5 border-b border-stone-200 bg-white shrink-0">
                <WikiAiAvatar size={30} />
                <div className="flex-1 min-w-0">
                    <div className="text-[13px] wiki-display font-bold text-stone-900 flex items-center gap-1.5">{dept?.name} Agent <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 wiki-pulse" /></div>
                    <div className="text-[10.5px] text-stone-500 truncate">Grounded in {dept?.name} pages you can access · cites sources</div>
                </div>
                <button onClick={onToggleFull} title={fullPage ? 'Dock to side' : 'Open as full page'} className="w-8 h-8 rounded-lg hover:bg-stone-100 text-stone-600 flex items-center justify-center shrink-0">{fullPage ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}</button>
                <button onClick={onClose} title="Close agent" className="w-8 h-8 rounded-lg hover:bg-stone-100 text-stone-600 flex items-center justify-center shrink-0"><X className="w-4 h-4" /></button>
            </div>

            <div ref={bodyRef} className={`flex-1 overflow-auto space-y-4 ${docked ? 'p-4' : 'p-5'}`}>
                {msgs.length === 0 ? (
                    <div className={`mx-auto ${docked ? 'pt-2' : 'max-w-[560px] pt-6'}`}>
                        <div className="text-center">
                            <div className="flex justify-center"><WikiAiAvatar size={fullPage ? 44 : 38} /></div>
                            <div className={`wiki-display font-bold text-stone-900 mt-3 ${fullPage ? 'text-[18px]' : 'text-[15px]'}`}>Ask the {dept?.name} Agent</div>
                            <div className="text-[12.5px] text-stone-500 mt-1 leading-snug">It only reads {dept?.name} pages you have access to, and links every claim back to the source.</div>
                        </div>
                        <div className="mt-5 space-y-2">
                            {suggestions.map((q, i) => (
                                <button key={i} onClick={() => send(q)} className="w-full text-left px-3 py-2.5 rounded-xl border border-stone-200 bg-white hover:border-stone-400 hover:bg-stone-100/30 text-[13px] text-stone-600 flex items-start gap-2">
                                    <MessageCircleQuestion className="w-4 h-4 text-stone-600 shrink-0 mt-0.5" /><span className="flex-1">{q}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : msgs.map((m, i) => <Bubble key={i} m={m} userName={userName} onOpenDoc={onOpenDoc} />)}
            </div>
            <Composer onSend={send} placeholder={`Ask the ${dept?.name} Agent…`} />
        </div>
    );
}
